import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { money2, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Package, Send, Check, AlertTriangle, PackageSearch, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared";
import { InflowProductPicker } from "@/components/InflowProductPicker";
import { cn } from "@/lib/utils";

const uid = () => "li_" + Math.random().toString(36).slice(2, 12);

export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [gstInclusive, setGstInclusive] = useState(false);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  // inFlow push flow
  const [pushOpen, setPushOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  const enquiry = useQuery({ queryKey: ["enquiry", id], queryFn: async () => (await client.get(`/enquiries/${id}`)).data });
  const inflow = useQuery({ queryKey: ["inflow-status"], queryFn: async () => (await client.get("/inflow/status")).data });
  const inflowEnabled = !!inflow.data?.enabled;
  const pushRecord = useQuery({ queryKey: ["inflow-push", id], queryFn: async () => (await client.get(`/inflow/enquiries/${id}/push-record`)).data });

  useEffect(() => {
    if (enquiry.data) {
      setTitle(enquiry.data.title || "");
      setGstInclusive(!!enquiry.data.gst_inclusive);
      setItems(enquiry.data.items || []);
    }
  }, [enquiry.data]);

  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((arr) => [...arr, { id: uid(), name: "", qty: 1, unit_price: 0, notes: "", inflow_product_id: null, inflow_product_name: null, sku: null }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const copyNum = async (n) => { try { await navigator.clipboard.writeText(n); toast.success("Invoice number copied"); } catch { toast.error("Copy failed"); } };

  const rate = 10; // Standard Australian GST — fixed
  const gross = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const taxAmount = gstInclusive ? gross - gross / (1 + rate / 100) : gross * rate / 100;
  const netSubtotal = gstInclusive ? gross - taxAmount : gross;
  const total = gstInclusive ? gross : gross + taxAmount;

  const cleanItems = () =>
    items
      .filter((it) => (it.name || it.inflow_product_name || "").trim())
      .map((it) => ({
        id: it.id || uid(),
        name: (it.inflow_product_name || it.name || "").trim(),
        qty: Number(it.qty) || 0,
        unit_price: Number(it.unit_price) || 0,
        notes: it.notes || null,
        sku: it.sku || null,
        inflow_product_id: it.inflow_product_id || null,
        inflow_product_name: it.inflow_product_name || null,
      }));

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/enquiries/${id}`, { title: title || "Showroom Visit", tax_rate: 10, gst_inclusive: gstInclusive, items: cleanItems() });
      toast.success("Quote saved");
      qc.invalidateQueries({ queryKey: ["enquiry", id] });
      qc.invalidateQueries({ queryKey: ["customer"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      const cid = enquiry.data?.customer?.id;
      if (cid) qc.invalidateQueries({ queryKey: ["enquiries", cid] });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save quote");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openPush = async () => {
    setPushResult(null);
    setPreview(null);
    setPreviewing(true);
    setPushOpen(true);
    const ok = await save(); // persist current lines so the preview reflects the latest
    if (!ok) { setPushOpen(false); setPreviewing(false); return; }
    try {
      const { data } = await client.get(`/inflow/enquiries/${id}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build inFlow preview");
      setPushOpen(false);
    } finally {
      setPreviewing(false);
    }
  };

  const confirmPush = async () => {
    setPushing(true);
    try {
      const { data } = await client.post(`/inflow/enquiries/${id}/push`);
      setPushResult(data);
      qc.invalidateQueries({ queryKey: ["enquiry", id] });
      qc.invalidateQueries({ queryKey: ["inflow-push", id] });
      const num = data.order_number;
      toast.success(
        data.status === "already_created" ? `Already in inFlow · ${num}` : `Invoice ${num} pushed to inFlow`,
        num ? { description: `Invoice number ${num}`, action: { label: "Copy", onClick: () => copyNum(num) } } : undefined
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push to inFlow failed");
    } finally {
      setPushing(false);
    }
  };

  if (enquiry.isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-muted/50" />;
  if (enquiry.isError) return <ErrorState onRetry={() => enquiry.refetch()} message="We couldn't load this enquiry. Please try again." />;
  if (!enquiry.data) return <div className="py-20 text-center text-muted-foreground">Enquiry not found.</div>;

  const cust = enquiry.data.customer;
  const anyUnmapped = inflowEnabled && items.length > 0 && items.some((it) => !it.inflow_product_id);

  return (
    <div className="animate-fade-up pb-24 md:pb-0">
      <button onClick={() => navigate(cust ? `/customers/${cust.id}` : -1)} data-testid="back-btn" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> {cust ? `Back to ${cust.name}` : "Back"}
      </button>

      {pushRecord.data?.pushed && (
        <div data-testid="push-history" className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-success/30 bg-[#059669]/[0.06] px-4 py-2.5 text-sm">
          <span className="flex items-center gap-1.5 font-semibold text-success"><CheckCircle2 className="h-4 w-4" /> Pushed to inFlow</span>
          <span className="flex items-center gap-1 text-muted-foreground">Invoice
            <button onClick={() => copyNum(pushRecord.data.record.order_number)} data-testid="copy-order-number" className="inline-flex items-center gap-1 font-semibold text-ink hover:text-primary"><span data-testid="push-order-number">{pushRecord.data.record.order_number}</span><Copy className="h-3 w-3" /></button>
          </span>
          <span className="text-muted-foreground">by <span className="text-ink">{pushRecord.data.record.pushed_by_name || "team"}</span></span>
          <span className="text-muted-foreground">on {fmtDate(pushRecord.data.record.created_at, "d MMM yyyy, h:mmaaa")}</span>
          {pushRecord.data.record.created_customer && <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-accent-foreground">new customer created</span>}
        </div>
      )}

      <div className="rounded-2xl border border-border bg-white shadow-[0_2px_8px_rgba(35,52,90,0.04)]">
        {/* Header */}
        <div className="border-b border-border p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="enquiry-title-input"
                className="border-0 px-0 font-display text-xl font-extrabold text-ink shadow-none focus-visible:ring-0" placeholder="Enquiry title" />
              {cust && <Link to={`/customers/${cust.id}`} className="text-sm text-muted-foreground hover:text-primary">for {cust.name}{cust.company ? ` · ${cust.company}` : ""}</Link>}
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-sm font-bold text-ink">Line items</h2>
            <Button onClick={addItem} size="sm" variant="outline" data-testid="add-item-btn" className="gap-1.5"><Plus className="h-4 w-4" />Add item</Button>
          </div>

          {items.length === 0 ? (
            <div className="flex flex-col items-center rounded-xl border border-dashed border-border py-12 text-center">
              <Package className="mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No tiles added yet.</p>
              <Button onClick={addItem} size="sm" className="mt-4 gap-1.5" data-testid="empty-add-item-btn"><Plus className="h-4 w-4" />Add first item</Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="hidden grid-cols-12 gap-3 px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:grid">
                <div className="col-span-4">{inflowEnabled ? "inFlow product" : "Tile / product"}</div>
                <div className="col-span-1 text-right">Qty</div>
                <div className="col-span-2 text-right">Unit price</div>
                <div className="col-span-3">Notes</div>
                <div className="col-span-2 text-right">Line total</div>
              </div>
              {items.map((it, idx) => (
                <div key={it.id || idx} data-testid={`line-item-${idx}`} className="grid grid-cols-12 items-center gap-3 rounded-lg border border-border p-3">
                  <div className="col-span-12 md:col-span-4">
                    {inflowEnabled ? (
                      <InflowProductPicker item={it} onSelect={(patch) => setItem(idx, patch)} />
                    ) : (
                      <Input value={it.name} onChange={(e) => setItem(idx, { name: e.target.value })} placeholder="e.g. Matte White 600x600" data-testid={`item-name-${idx}`} />
                    )}
                  </div>
                  <div className="col-span-3 md:col-span-1">
                    <Input type="number" min="0" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} className="text-right" data-testid={`item-qty-${idx}`} />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: e.target.value })} className="text-right" data-testid={`item-price-${idx}`} />
                  </div>
                  <div className="col-span-5 md:col-span-3">
                    <Input value={it.notes || ""} onChange={(e) => setItem(idx, { notes: e.target.value })} placeholder="Size, colour, room…" data-testid={`item-notes-${idx}`} />
                  </div>
                  <div className="col-span-12 flex items-center justify-end gap-1 md:col-span-2">
                    <span className="font-display text-sm font-bold text-ink">{money2((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</span>
                    <button onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`} className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-error">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {anyUnmapped && (
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#D97706]/10 px-3 py-2 text-xs font-medium text-warning" data-testid="unmapped-warning">
              <PackageSearch className="h-4 w-4" /> Some lines aren't linked to an inFlow product yet — pick one before pushing.
            </div>
          )}

          {/* Totals */}
          <div className="mt-6 flex justify-end">
            <div className="w-full max-w-sm space-y-3 rounded-xl bg-muted/50 p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pricing</span>
                <div className="inline-flex rounded-lg border border-border bg-white p-0.5" data-testid="gst-mode-toggle">
                  <button type="button" onClick={() => setGstInclusive(false)} data-testid="gst-exclusive-btn"
                    className={cn("rounded-md px-2.5 py-1 text-xs font-semibold transition-colors", !gstInclusive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-ink")}>Excl. GST</button>
                  <button type="button" onClick={() => setGstInclusive(true)} data-testid="gst-inclusive-btn"
                    className={cn("rounded-md px-2.5 py-1 text-xs font-semibold transition-colors", gstInclusive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-ink")}>Incl. GST</button>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{gstInclusive ? "Subtotal (ex GST)" : "Subtotal"}</span><span className="font-medium text-ink" data-testid="subtotal">{money2(netSubtotal)}</span></div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">GST (10%){gstInclusive ? " incl." : ""}</span>
                <span className="font-medium text-ink" data-testid="tax-amount">{money2(taxAmount)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-2">
                <span className="font-display font-bold text-ink">{gstInclusive ? "Total (inc GST)" : "Total"}</span>
                <span className="font-display text-lg font-extrabold text-primary" data-testid="total">{money2(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="fixed inset-x-0 bottom-16 z-20 border-t border-border bg-[#F8FAFC]/85 p-3 backdrop-blur-xl md:static md:mt-4 md:border-0 md:bg-transparent md:p-0 md:backdrop-blur-none">
        <div className="mx-auto flex max-w-6xl items-center justify-end gap-3 md:px-0">
          <div className="mr-auto text-sm text-muted-foreground md:hidden">Total <span className="font-display font-bold text-ink">{money2(total)}</span></div>
          {inflowEnabled && (
            <Button onClick={openPush} disabled={saving || items.length === 0} variant="outline" data-testid="push-inflow-btn" className="gap-2">
              <Send className="h-4 w-4" /> Push to inFlow
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="save-quote-btn" className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save quote
          </Button>
        </div>
      </div>

      {/* Push-to-inFlow preview dialog */}
      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent data-testid="push-inflow-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Push invoice to inFlow</DialogTitle>
            <DialogDescription>Creates an invoice in inFlow. This deducts stock and syncs to your accounting (Xero) straight away.</DialogDescription>
          </DialogHeader>

          {previewing ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Building preview…</div>
          ) : pushResult ? (
            <div className="py-4 text-center" data-testid="push-success">
              <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-success" />
              <p className="font-display text-base font-bold text-ink">{pushResult.status === "already_created" ? "Already in inFlow" : "Invoice created in inFlow"}</p>
              <p className="mt-1 text-sm text-muted-foreground">Quote <button onClick={() => copyNum(pushResult.order_number)} data-testid="copy-success-order-number" className="inline-flex items-center gap-1 font-semibold text-ink hover:text-primary">{pushResult.order_number}<Copy className="h-3 w-3" /></button> for {pushResult.inflow_customer_name}</p>
              <p className="mt-3 text-xs text-muted-foreground">It's been invoiced in inFlow — stock and Xero are updated automatically.</p>
            </div>
          ) : preview ? (
            <div className="space-y-4">
              {preview.already_pushed ? (
                <div className="flex items-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm text-accent-foreground">
                  <Check className="h-4 w-4" /> Already pushed as <span className="font-semibold">{preview.already_pushed.order_number}</span>.
                </div>
              ) : (
                <>
                  <div className="rounded-lg border border-border p-3 text-sm">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Customer</div>
                    <div className="mt-1 font-semibold text-ink">{preview.customer.display_name}</div>
                    <div className="text-xs text-muted-foreground">{preview.customer.will_create ? "Will be created in inFlow" : preview.customer.matched ? "Matched an existing inFlow customer" : "Will match or create on push"}</div>
                  </div>
                  <div className="rounded-lg border border-border">
                    <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{preview.lines.length} line item(s)</div>
                    <div className="max-h-52 divide-y divide-border overflow-auto">
                      {preview.lines.map((l, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 text-sm">
                          {l.mapped ? <Check className="h-4 w-4 shrink-0 text-success" /> : <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />}
                          <span className="min-w-0 flex-1 truncate">{l.inflow_product_name || l.name || "(unnamed)"}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">{l.qty} × {money2(l.unit_price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {preview.gst_inclusive && (
                    <p className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground" data-testid="gst-note">
                      Prices are sent to inFlow <b>excluding GST</b>; inFlow adds {preview.tax_rate}% GST so the customer total matches your GST-inclusive pricing.
                    </p>
                  )}
                  {!preview.all_mapped && (
                    <div className="flex items-center gap-2 rounded-lg bg-[#D97706]/10 px-3 py-2 text-xs font-medium text-warning">
                      <AlertTriangle className="h-4 w-4" /> {preview.unmapped_count} line(s) aren't linked to an inFlow product. Map them, save, then push.
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setPushOpen(false)} data-testid="push-close-btn">Close</Button>
            {preview && !preview.already_pushed && !pushResult && (
              <Button onClick={confirmPush} disabled={pushing || !preview.all_mapped} data-testid="push-confirm-btn" className="gap-2">
                {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Create inFlow invoice
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
